import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { ButtonProps } from './Button/ButtonWidget';
import { DataAttributes } from '../utils/DataAttributes';
import { conditional, makeTestId } from '../utils/Declarative';
import { findIndex } from '../utils/FindIndex';

export enum SelectableButtonGroupSelectionBehavior {
  /** Multiple buttons can be selected within the group. */
  Multiple = 'Multiple',
  /** A single button (at most) can be selected within the group. */
  Single = 'Single',
}

export interface SelectableButtonGroupProps {
  /**
   * Array of `ButtonWidget` which will be rendered within the group. The following props on each child will be internally overridden:
   * `disableTabAccessibility`, `handleClick`, `ref`, `role`, `selected`.
   */
  children?: Array<React.ReactElement<ButtonProps>>;

  /** The function that will be called when a button's selection state changes. */
  onChange?: (index: number, selected: boolean) => void;

  /**
   * The selection behavior for the button group. Options include:
   *
   *   - SelectableButtonGroupSelectionBehavior.Multiple: multiple buttons can be selected
   *   - SelectableButtonGroupSelectionBehavior.Single: a single button (at most) can be selected
   *
   * NOTE: For SelectableButtonGroupSelectionBehavior.Single select behavior, if multiple buttons are
   * selected, single selectiveness is not enforced.
   */
  selectionBehavior: SelectableButtonGroupSelectionBehavior;

  /** The overriding style of the button group element. */
  style?: React.CSSProperties;

  /** Test id to be attached to the button group. '-SelectableButtonGroup' will be automatically added as a suffix. */
  testId: string;

  /** A description of what the button group represents. */
  title: string;
}

export interface SelectableButtonGroupState {
  /** The index of the button that currently has focus, or should receive focus when navigating to this widget. */
  focusIndex: number;
}

export class SelectableButtonGroupWidget extends React.Component<SelectableButtonGroupProps, SelectableButtonGroupState> {
  private childButtons: React.ReactElement<ButtonProps>[] = [];
  private buttonElements: HTMLElement[] = [];
  private handleButtonRefs: ((ref: HTMLElement) => void)[] = [];

  public constructor(props: SelectableButtonGroupProps) {
    super(props);

    this.updateChildButtons();
    this.state = { focusIndex: this.getInitialFocusIndex() };
  }

  private updateChildButtons(): void {
    const childButtons: React.ReactElement<ButtonProps>[] = [];
    let selectedButtonsCount = 0;

    React.Children.map(this.props.children, (child: React.ReactElement<ButtonProps>) => {
      if (child.props.buttonType !== undefined) {
        childButtons.push(child);
        if (child.props.selected) { selectedButtonsCount++; }
      }
    });

    if (childButtons.length < 2) {
      throw Error(`Invalid button count: 2+ are required`);
    }

    if (this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single && selectedButtonsCount > 1) {
      // tslint:disable-next-line max-line-length
      throw Error(`Invalid selected button count: SelectableButtonGroupSelectionBehavior.Single requires one or zero buttons to be selected. ${selectedButtonsCount} are selected`);
    }

    this.childButtons = childButtons;
  }

  private getInitialFocusIndex(): number {
    let focusIndex = findIndex(this.childButtons, button => !!button.props.selected && !button.props.disabled);
    if (focusIndex < 0) {
      focusIndex = findIndex(this.childButtons, button => !button.props.disabled);
    }
    return focusIndex;
  }

  private isButtonSelected(index: number): boolean {
    return !!this.childButtons[index].props.selected;
  }

  private handleButtonRefWrapper = (buttonIndex: number): ((ref: HTMLElement | null) => void) => {
    if (this.handleButtonRefs[buttonIndex] === undefined) {
      this.handleButtonRefs[buttonIndex] = (ref: HTMLElement) => {
        this.buttonElements[buttonIndex] = ReactDOM.findDOMNode(ref) as HTMLElement;
      };
    }

    return this.handleButtonRefs[buttonIndex];
  }

  private handleClick = (e: React.SyntheticEvent<HTMLDivElement>): void => {
    const buttonElement = e.currentTarget as HTMLElement;
    const buttonIndex = findIndex(this.childButtons, (button, index) => buttonElement === this.buttonElements[index]);
    const selected = this.childButtons[buttonIndex].props.selected;

    if (this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single && selected) { return; }

    if (this.props.onChange) {
      this.props.onChange(buttonIndex, !selected);
    }
  }

  private handleFocus = (e: React.FocusEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement;
    const focusIndex = findIndex(this.childButtons, (button, index) => target === this.buttonElements[index] && !button.props.disabled);

    if (focusIndex >= 0 && focusIndex !== this.state.focusIndex) {
      this.setState({ focusIndex });
    }
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const buttonCount = this.childButtons.length;

    let increment = 0;
    let focusIndex = this.state.focusIndex;

    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      increment = -1;
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      increment = 1;
    } else if (e.key === 'Home') {
      focusIndex = -1;
      increment = 1;
    } else if (e.key === 'End') {
      focusIndex = buttonCount;
      increment = -1;
    } else {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    do {
      focusIndex = (focusIndex + increment + buttonCount) % buttonCount;
      if (focusIndex === this.state.focusIndex) { return; }
    } while (this.childButtons[focusIndex].props.disabled);

    const isSingleSelect = this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single;
    if (isSingleSelect && !this.isButtonSelected(focusIndex) && this.props.onChange) {
      this.props.onChange(focusIndex, true);
    }

    if (focusIndex !== this.state.focusIndex) {
      this.setState({ focusIndex }, () => {
        if (this.buttonElements[this.state.focusIndex] !== undefined) {
          this.buttonElements[this.state.focusIndex].focus();
        }
      });
    }
  }

  private renderChildren(): JSX.Element[] {
    let buttonIndex = 0;

    return React.Children.map(this.props.children, (child: React.ReactElement<ButtonProps>) => {
      if (child.props.buttonType === undefined) {
        return child;
      }

      const buttonPropsOverride: Partial<ButtonProps> | React.ClassAttributes<HTMLDivElement> = {
        disableTabAccessibility: this.state.focusIndex !== buttonIndex,
        handleClick: this.handleClick,
        key: buttonIndex,
        ref: this.handleButtonRefWrapper(buttonIndex),
        role: this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single ? 'radio' : 'button',
        selected: !!child.props.selected
      };

      buttonIndex++;

      return React.cloneElement(child, buttonPropsOverride);
    });
  }

  public render(): JSX.Element {
    this.updateChildButtons();

    const isSingleSelect = this.props.selectionBehavior === SelectableButtonGroupSelectionBehavior.Single;

    const divProps: React.HTMLAttributes<HTMLDivElement> & DataAttributes = {
      'data-tb-test-id': makeTestId(this.props.testId, 'SelectableButtonGroup'),
      onFocus: this.handleFocus,
      onKeyDown: this.handleKeyDown,
      role: isSingleSelect ? 'radiogroup' : undefined,
      style: this.props.style,
      title: this.props.title,
      ...conditional(!isSingleSelect, { 'aria-multiselectable': true })
    };

    return (
      <div {...divProps}>
        {this.renderChildren()}
      </div>
    );
  }
}
